import { useForm } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import z from "zod";

import Loader from "@/components/loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth-client";

export const Route = createFileRoute("/login")({
  component: PaginaLogin,
});

// El mismo esqueleto que el login del ERP: branding a la izquierda, formulario
// a la derecha. Cambian las cifras, que acá describen el módulo.
const CIFRAS = [
  { label: "Empresas", value: "26" },
  { label: "Cuentas", value: "100" },
  { label: "Bancos", value: "4" },
];

function PaginaLogin() {
  const navigate = useNavigate({ from: "/login" });
  const { isPending } = authClient.useSession();

  const form = useForm({
    defaultValues: { email: "", password: "" },
    onSubmit: async ({ value }) => {
      await authClient.signIn.email(
        { email: value.email, password: value.password },
        {
          onSuccess: async () => {
            // Se espera a que la sesión esté en el store ANTES de navegar.
            // Sin esto el login funciona, muestra «Sesión iniciada» y rebota
            // al login: la portada pregunta por la sesión antes de que el
            // store se haya enterado, la ve vacía y expulsa. Parece un fallo
            // de credenciales y no lo es.
            await authClient.getSession();
            navigate({ to: "/" });
            toast.success("Sesión iniciada");
          },
          onError: ({ error }) => {
            if (error.status === 401) {
              toast.error("Correo o contraseña incorrectos");
              return;
            }
            // Si la API está caída, el error llega sin message ni statusText y
            // el aviso saldría vacío, como si no hubiera pasado nada.
            toast.error(
              error.message?.trim() ||
                error.statusText?.trim() ||
                "No se pudo contactar al servidor. Revisá que esté levantado.",
            );
          },
        },
      );
    },
    validators: {
      onSubmit: z.object({
        email: z.email("Correo inválido"),
        password: z.string().min(8, "Mínimo 8 caracteres"),
      }),
    },
  });

  if (isPending) return <Loader />;

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      {/* Lado izquierdo — identidad */}
      <div className="relative hidden overflow-hidden bg-slate-900 lg:flex">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        {/* Trama sutil, para que el panel no quede como un plano liso */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        <div className="relative z-10 flex w-full flex-col justify-between p-12 text-white">
          {/* El distintivo dice la verdad sobre DÓNDE se está entrando.
              Puesto a mano decía «Entorno de desarrollo» también en la máquina
              de producción, que es justo al revés de para lo que sirve: quien
              lo lee tiene que saber si lo que va a tocar son los datos buenos. */}
          {import.meta.env.DEV ? (
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 backdrop-blur-sm">
              <div className="h-2 w-2 animate-pulse rounded-full bg-cyan-400" />
              <span className="font-medium text-sm">Entorno de desarrollo</span>
            </div>
          ) : (
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 backdrop-blur-sm">
              <div className="h-2 w-2 rounded-full bg-emerald-400" />
              <span className="font-medium text-sm">Grupo Ecoplaza</span>
            </div>
          )}

          <div className="space-y-6">
            <div className="space-y-2">
              <h1 className="font-bold text-6xl tracking-tight">
                Conciliación
              </h1>
              <div className="h-1 w-24 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500" />
            </div>
            <p className="max-w-md text-slate-300 text-xl leading-relaxed">
              Estados de cuenta, comprobantes y vouchers cruzados con evidencia
              — no a ojo.
            </p>
          </div>

          <div className="grid max-w-md grid-cols-3 gap-4">
            {CIFRAS.map((c) => (
              <div
                key={c.label}
                className="rounded-lg border border-white/10 bg-white/5 p-4 backdrop-blur-sm"
              >
                <div className="font-bold text-2xl text-cyan-400">
                  {c.value}
                </div>
                <div className="mt-1 text-slate-400 text-xs">{c.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lado derecho — acceso */}
      <div className="flex items-center justify-center bg-background p-8">
        <div className="w-full max-w-md">
          <div className="mb-6 text-center lg:hidden">
            <h1 className="font-bold text-4xl text-foreground">Conciliación</h1>
            <div className="mx-auto mt-2 h-1 w-16 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600" />
          </div>

          <div className="rounded-xl border bg-card p-8 shadow-sm">
            <div className="mb-8 text-center">
              <h2 className="font-bold text-2xl">Iniciar sesión</h2>
              <p className="mt-1 text-muted-foreground text-sm">
                Accedé con tu cuenta corporativa
              </p>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                form.handleSubmit();
              }}
              className="space-y-4"
            >
              <form.Field name="email">
                {(field) => (
                  <div className="space-y-1.5">
                    <Label htmlFor={field.name}>Correo</Label>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="email"
                      autoComplete="email"
                      placeholder="usuario@empresa.com"
                      className="h-11"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                    {field.state.meta.errors.map((error) => (
                      <p
                        key={error?.message}
                        className="text-destructive text-xs"
                      >
                        {error?.message}
                      </p>
                    ))}
                  </div>
                )}
              </form.Field>

              <form.Field name="password">
                {(field) => (
                  <div className="space-y-1.5">
                    <Label htmlFor={field.name}>Contraseña</Label>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="password"
                      autoComplete="current-password"
                      placeholder="••••••••"
                      className="h-11"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                    {field.state.meta.errors.map((error) => (
                      <p
                        key={error?.message}
                        className="text-destructive text-xs"
                      >
                        {error?.message}
                      </p>
                    ))}
                  </div>
                )}
              </form.Field>

              <form.Subscribe>
                {(state) => (
                  <Button
                    type="submit"
                    size="lg"
                    className="h-11 w-full text-base"
                    disabled={!state.canSubmit || state.isSubmitting}
                  >
                    {state.isSubmitting ? "Ingresando…" : "Ingresar"}
                  </Button>
                )}
              </form.Subscribe>
            </form>
          </div>

          <p className="mt-4 text-center text-muted-foreground text-xs">
            Las cuentas las crea un administrador. No hay registro abierto.
          </p>
        </div>
      </div>
    </div>
  );
}
